import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Heart, Share2, ArrowRight, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import apiClient, { paymentAPI, campaignAPI } from '../services/api';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../context/LanguageContext';

const DonationSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [error, setError] = useState(null);
  const { t } = useLanguage();

  const sessionId = searchParams.get('session_id');
  const campaignId = searchParams.get('campaign_id');
  const isPayPal = searchParams.get('paypal') === 'true';
  const paypalToken = searchParams.get('token'); // PayPal adds this after approval

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 10; // Increased retries for webhook race condition
    const retryDelay = 2000; // 2 seconds
    let isMounted = true;

    // Handle PayPal callback
    const handlePayPalCallback = async () => {
      try {
        // Get pending PayPal donation data from sessionStorage
        const pendingDonation = sessionStorage.getItem('pendingPayPalDonation');
        
        if (!pendingDonation && !paypalToken) {
          setError('PayPal payment data not found. If you were charged, please contact support.');
          setLoading(false);
          return;
        }

        const donationData = pendingDonation ? JSON.parse(pendingDonation) : null;
        const orderId = paypalToken || donationData?.order_id;

        if (!orderId) {
          setError('PayPal order not found. If you were charged, please contact support.');
          setLoading(false);
          return;
        }

        // Capture the PayPal order
        const captureResponse = await apiClient.post('/paypal/capture-order', {
          order_id: orderId,
          campaign_id: donationData?.campaign_id || campaignId,
          donor_name: donationData?.donor_name || 'Anonymous',
          donor_email: donationData?.donor_email || '',
          message: donationData?.message || '',
          is_anonymous: donationData?.is_anonymous || false
        });

        if (!isMounted) return;

        if (captureResponse.data.success) {
          // Clear pending donation
          sessionStorage.removeItem('pendingPayPalDonation');
          
          setPaymentStatus({
            payment_status: 'paid',
            status: 'complete',
            amount: captureResponse.data.amount,
            currency: captureResponse.data.currency,
            donation_id: captureResponse.data.donation_id,
            donor_name: donationData?.donor_name || 'Anonymous',
            is_anonymous: donationData?.is_anonymous || false
          });
          
          // Fetch campaign details
          const campId = donationData?.campaign_id || campaignId;
          if (campId) {
            try {
              const campaignData = await campaignAPI.get(campId);
              setCampaign(campaignData);
            } catch (campErr) {
              console.error('Error fetching campaign:', campErr);
            }
          }
        } else {
          setError(captureResponse.data.message || 'PayPal payment was not completed');
        }
        setLoading(false);
      } catch (err) {
        console.error('PayPal capture error:', err);
        if (!isMounted) return;
        
        // Check if already captured (duplicate request)
        if (err.response?.status === 422 || err.response?.data?.detail?.includes('already captured')) {
          setPaymentStatus({
            payment_status: 'paid',
            status: 'complete'
          });
          sessionStorage.removeItem('pendingPayPalDonation');
        } else {
          setError(err.response?.data?.detail || 'Failed to complete PayPal payment. If you were charged, please contact support.');
        }
        setLoading(false);
      }
    };

    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setError('No payment session found');
        setLoading(false);
        return;
      }

      try {
        // Check payment status
        const status = await paymentAPI.getCheckoutStatus(sessionId);
        
        if (!isMounted) return;
        
        // Check if payment is complete (paid status)
        if (status.payment_status === 'paid' || status.status === 'complete') {
          setPaymentStatus(status);
          setLoading(false);
          
          // Get campaign details
          if (campaignId) {
            try {
              const campaignData = await campaignAPI.get(campaignId);
              setCampaign(campaignData);
            } catch (campErr) {
              console.error('Error fetching campaign:', campErr);
            }
          }
        } else if (retryCount < maxRetries) {
          // Payment not yet confirmed - webhook might still be processing
          retryCount++;
          console.log(`Payment status: ${status.status}/${status.payment_status} - Retrying (${retryCount}/${maxRetries})...`);
          setTimeout(checkPaymentStatus, retryDelay);
        } else {
          // Max retries reached but we have a status - show it
          // For Stripe, if session exists, payment likely went through
          console.log('Max retries reached, showing current status:', status);
          
          // If we got here and status exists, treat as success if Stripe says complete
          if (status.status === 'complete' || status.amount > 0) {
            // Stripe session completed - treat as success even if webhook delayed
            setPaymentStatus({
              ...status,
              payment_status: 'paid' // Force success display
            });
          } else {
            setPaymentStatus(status);
          }
          setLoading(false);
          
          // Still try to get campaign
          if (campaignId) {
            try {
              const campaignData = await campaignAPI.get(campaignId);
              setCampaign(campaignData);
            } catch (campErr) {
              console.error('Error fetching campaign:', campErr);
            }
          }
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        
        if (!isMounted) return;
        
        // If 404 or any error, the transaction might still be processing - retry
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`Error occurred - Retrying payment status check (${retryCount}/${maxRetries})...`);
          setTimeout(checkPaymentStatus, retryDelay);
          return;
        }
        
        // Max retries exhausted
        // Show a user-friendly message that doesn't alarm them
        setError('Payment verification is taking longer than expected. If you were charged, your donation has been received and will appear shortly. Please check your email for confirmation.');
        setLoading(false);
      }
    };

    // Call appropriate handler based on payment type
    if (isPayPal || paypalToken) {
      handlePayPalCallback();
    } else if (sessionId) {
      checkPaymentStatus();
    } else {
      setError('No payment information found');
      setLoading(false);
    }
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [sessionId, campaignId, isPayPal, paypalToken]);

  const handleShare = () => {
    if (navigator.share && campaign) {
      navigator.share({
        title: `I just donated to ${campaign.title}!`,
        text: `I just made a donation to support this campaign. Join me in making a difference!`,
        url: `${window.location.origin}/campaign/${campaignId}`
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">Verifying your donation...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    // Even with an error, if payment might have succeeded, show optimistic success
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Thank You for Your Donation!
              </h1>
              <p className="text-muted-foreground mb-6">
                {error}
              </p>
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  If you see a charge on your card, your donation was successful! You'll receive a confirmation email shortly.
                </p>
              </div>
              <Link to="/discover">
                <Button className="w-full">
                  <ArrowRight className="w-4 h-4 mr-2" /> Explore Campaigns
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (!paymentStatus) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Something went wrong
              </h1>
              <p className="text-muted-foreground mb-6">
                {error || 'We couldn\'t verify your payment. Please contact support if you were charged.'}
              </p>
              <Link to="/">
                <Button>{t('exploreCampaigns')}</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const isPaid = paymentStatus.payment_status === 'paid';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="pt-8 pb-8 text-center">
            {isPaid ? (
              <>
                {/* Success Animation */}
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-12 h-12 text-green-600" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center animate-bounce">
                    <Heart className="w-4 h-4 text-emerald-500 fill-emerald-500" />
                  </div>
                </div>

                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Thank You! 🎉
                </h1>
                <p className="text-lg text-muted-foreground mb-6">
                  Your donation has been received successfully
                </p>

                {/* Donation Details */}
                <div className="bg-muted/50 rounded-xl p-6 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-left">
                    <div>
                      <p className="text-sm text-muted-foreground">Amount</p>
                      <p className="text-2xl font-bold text-foreground">
                        ${paymentStatus.amount?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Donor</p>
                      <p className="text-lg font-semibold text-foreground">
                        {paymentStatus.is_anonymous ? 'Anonymous' : paymentStatus.donor_name}
                      </p>
                    </div>
                  </div>
                  {campaign && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-sm text-muted-foreground">Campaign</p>
                      <p className="font-semibold text-foreground">{campaign.title}</p>
                    </div>
                  )}
                </div>

                {/* Impact Message */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 rounded-xl p-4 mb-6">
                  <p className="text-sm text-muted-foreground">
                    Your generosity makes a real difference. Every donation brings us closer to our goal!
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handleShare}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  {campaignId && (
                    <Link to={`/campaign/${campaignId}`} className="flex-1">
                      <Button className="w-full bg-gradient-to-r from-emerald-500 to-teal-600">
                        View Campaign <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  )}
                </div>
              </>
            ) : paymentStatus.status === 'complete' ? (
              // Stripe session completed but webhook delayed - show success anyway
              <>
                <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                </div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  {t?.donation?.thankYou || 'Thank You!'}
                </h1>
                <p className="text-muted-foreground mb-6">
                  Your payment has been received successfully.
                  {paymentStatus.donor_email ? 
                    ' A confirmation email will be sent to your email address.' : 
                    ' Your donation has been recorded successfully.'}
                </p>

                {/* Donation Details */}
                <div className="bg-secondary/50 rounded-xl p-4 mb-6 text-left">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="text-2xl font-bold text-emerald-500">
                      ${paymentStatus.amount?.toFixed(2) || paymentStatus.donation_amount?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {campaignId && (
                    <Link to={`/campaign/${campaignId}`} className="flex-1">
                      <Button className="w-full bg-gradient-to-r from-emerald-500 to-teal-600">
                        View Campaign <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-12 h-12 text-yellow-600 animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  Payment Processing
                </h1>
                <p className="text-muted-foreground mb-6">
                  Your payment is being processed. This page will update automatically.
                </p>
                <p className="text-sm text-muted-foreground">
                  Status: {paymentStatus.status}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default DonationSuccessPage;
